// Step 5 (Bison) - the sending and warmup profile, and the run's tags.
//
// Twin of StepParams with Bison's much shorter field list. Same seeding rule
// (run.params when the run has been here before, CANONICAL_SQ_BISON_PARAMS when
// not; the canonical object is read-only), same string-held numeric state, same
// shared TagsPanel. The shell mounts this component only for bison runs.
//
// The form is short because the platform is: Bison has no sending gap, no
// ramp-up numbers and no reply rate - it ramps warmup itself. The params route
// parses a bison body with a STRICT schema, so the save below sends exactly
// these four keys and never a spread of a wider shape.

import { useState } from 'react';

import {
  CANONICAL_SQ_BISON_PARAMS,
  sqApi,
  sqErrorMessage,
  type SqBisonParams,
} from '../sequencerApi';
import { TagsPanel } from './TagsPanel';
import type { SqStepProps } from './shared';

type NumKey = 'daily_limit' | 'warmup_daily_limit';

/** Matches nonNegativeInt in the params route. */
const MAX_INT = 10_000;

const NUM_FIELDS: { key: NumKey; label: string; hint: string; max: number }[] = [
  {
    key: 'daily_limit',
    label: 'Daily limit',
    hint: 'Campaign emails per day, per mailbox.',
    max: MAX_INT,
  },
  {
    key: 'warmup_daily_limit',
    label: 'Warmup daily limit',
    hint: 'Warmup emails per day, per mailbox.',
    max: MAX_INT,
  },
];

/** Same rule as StepParams: a whole non-negative integer inside the field's own
 *  ceiling, or null. */
function parseNum(raw: string, max: number): number | null {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 && n <= max ? n : null;
}

export function StepParamsBison({
  runId,
  detail,
  reload,
  goToStep,
  toast,
}: SqStepProps): JSX.Element {
  const { run } = detail;

  // The cast is guarded by the shell: this component mounts only when
  // run.sequencer === 'bison', so run.params is the Bison shape.
  const [nums, setNums] = useState<Record<NumKey, string>>(() => {
    const seed = (run.params as SqBisonParams | null) ?? CANONICAL_SQ_BISON_PARAMS;
    return {
      daily_limit: String(seed.daily_limit),
      warmup_daily_limit: String(seed.warmup_daily_limit),
    };
  });

  const [enableWarmup, setEnableWarmup] = useState<'yes' | 'no'>(() => {
    const seed = (run.params as SqBisonParams | null) ?? CANONICAL_SQ_BISON_PARAMS;
    return seed.enable_warmup;
  });

  const [existingIds, setExistingIds] = useState<string[]>(
    () => [...(run.params?.extra_tags.existing_ids ?? [])],
  );
  const [newNames, setNewNames] = useState<string[]>(
    () => [...(run.params?.extra_tags.new_names ?? [])],
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    const parsed = {} as Record<NumKey, number>;
    for (const f of NUM_FIELDS) {
      const value = parseNum(nums[f.key], f.max);
      if (value === null) {
        setError(`${f.label} must be a whole number between 0 and ${f.max}.`);
        return;
      }
      parsed[f.key] = value;
    }

    const params: SqBisonParams = {
      daily_limit: parsed.daily_limit,
      warmup_daily_limit: parsed.warmup_daily_limit,
      enable_warmup: enableWarmup,
      // Same rule as the other two steps: `resolved_ids` is Start's alone.
      extra_tags: { existing_ids: [...existingIds], new_names: [...newNames] },
    };

    setBusy(true);
    setError(null);
    try {
      await sqApi.setParams(runId, params);
      await reload();
      await goToStep(6);
    } catch (err) {
      setError(sqErrorMessage(err));
      toast('error', sqErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sq-step">
      <div className="sq-step-head">
        <h2>Step 5 - Parameters</h2>
        <p>
          The sending and warmup profile pushed to every mailbox in this run, prefilled with our
          standard values. Bison ramps warmup on its own, so there is nothing else to set here.
          Change one only when the customer's order says so.
        </p>
      </div>
      <div className="sq-step-body">
        {error && <div className="sq-notice sq-notice--err">{error}</div>}

        <div className="sq-numgrid">
          {NUM_FIELDS.map((f) => (
            <div className="sq-field" key={f.key}>
              <label className="sq-field-label" htmlFor={`sq-param-${f.key}`}>
                {f.label}
              </label>
              <input
                id={`sq-param-${f.key}`}
                className="sq-input sq-mono"
                type="number"
                inputMode="numeric"
                min={0}
                max={f.max}
                step={1}
                value={nums[f.key]}
                onChange={(e) => setNums({ ...nums, [f.key]: e.target.value })}
                disabled={busy}
              />
              <span className="sq-hint">{f.hint}</span>
            </div>
          ))}
        </div>

        <div className="sq-togglegrid">
          <div className="sq-field">
            <span className="sq-field-label">Warmup</span>
            <div className="sq-toggle">
              {(['yes', 'no'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`sq-toggle-btn${enableWarmup === v ? ' sq-toggle-btn--on' : ''}`}
                  onClick={() => setEnableWarmup(v)}
                  disabled={busy}
                  aria-pressed={enableWarmup === v}
                >
                  {v === 'yes' ? 'Yes' : 'No'}
                </button>
              ))}
            </div>
            <span className="sq-hint">Run Bison warmup on every connected mailbox.</span>
          </div>
        </div>

        <TagsPanel
          runId={runId}
          tagName={run.tag_name}
          existingIds={existingIds}
          setExistingIds={setExistingIds}
          newNames={newNames}
          setNewNames={setNewNames}
          busy={busy}
        />

        <div className="sq-actions">
          <button
            type="button"
            className="sq-btn sq-btn--primary"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy ? 'Saving...' : 'Save & continue'}
          </button>
        </div>
      </div>
    </section>
  );
}
